<template>
  <div>
    <span class="profile-notifications">
      <v-menu
        v-model="notificationMenu"
        location="bottom start"
        max-width="420"
        :close-on-content-click="false"
      >
        <template #activator="{ props }">
          <!-- A row of the compact profile menu: same icon lane and title as the
               rows under it, with the top bar's green unread badge on the bell. -->
          <v-list-item
            v-if="$vuetify.display.mdAndDown"
            data-test="notifications-row"
            v-bind="props"
          >
            <template #prepend>
              <NotificationBadge :notifications-length="total">
                <v-icon>mdi-bell</v-icon>
              </NotificationBadge>
            </template>
            <v-list-item-title>Notifications</v-list-item-title>
          </v-list-item>
          <NotificationBadge v-else :notifications-length="total">
            <v-btn
              :color="total ? 'green' : 'grey-lighten-2'"
              icon
              size="small"
              width="40"
              height="40"
              class="text-white"
              :ripple="false"
              v-bind="props"
            >
              <v-icon>mdi-bell</v-icon>
            </v-btn>
          </NotificationBadge>
        </template>
        <v-card
          variant="outlined"
          class="mx-auto bg-surface"
          max-width="400"
        >
          <v-list-subheader>
            <span class="mr-16 white-space--nowrap">Notifications ({{ total }})</span>
            <v-spacer />
            <v-btn
              v-if="total"
              size="x-small"
              variant="outlined"
              class="ml-4"
              :loading="clearAllLoading"
              @click="clearAllNotifications"
            >
              Clear all
            </v-btn>
            <v-btn icon class="cancel" @click="notificationMenu = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-list-subheader>
          <v-divider />
          <v-list class="mb-0 pb-0 overflow-y-auto" style="max-height: 400px">
            <v-card-text v-if="!notifications.length && !loading" class="text-medium-emphasis">
              You're all caught up!
            </v-card-text>
            <div v-for="notification in notifications" :key="notification.id">
              <v-list-item
                :lines="notification.body ? 'three' : 'two'"
                :disabled="notification.show"
                @click="openLink(notification)"
              >
                <v-progress-linear v-if="checkIsLoading(notification.id)" indeterminate color="primary" />
                <template v-else>
                  <div>
                    <v-list-item-title :class="{ 'text-primary': notification.metadata && !notification.show }" style="white-space: initial;">
                      {{ notification.title }}
                    </v-list-item-title>
                    <v-list-item-subtitle
                      v-if="notification.body"
                      v-dompurify-html="notification.body"
                      style="white-space: initial;"
                      class="d-block"
                    />
                    <v-list-item-subtitle>
                      {{ $filters.formatDate(notification.created_at) }} {{ $filters.formatTime(notification.created_at) }}
                    </v-list-item-subtitle>
                  </div>
                </template>
                <template v-if="!checkIsLoading(notification.id)" #append>
                  <v-btn icon size="small" @click.stop="clearNotification(notification)">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </template>
              </v-list-item>
              <v-divider />
            </div>
            <AppLoadMoreLoader
              v-if="hasMore"
              @end:intersect="$emit('load:more')"
            />
          </v-list>
        </v-card>
      </v-menu>
    </span>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { mapState } from 'vuex';

import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { INotification } from '@/bundles/Notification/interfaces/INotification';
import { notificationService } from '@/bundles/Notification/factories/notificationServiceFactory';
import { useNotifications } from '@/bundles/App/composables/useNotifications';

import NotificationBadge from '@/bundles/Panels/components/NotificationBadge.vue';
import AppLoadMoreLoader from '@/bundles/App/components/inputs/AppLoadMoreLoader.vue';

interface INotificationMenu {
  notificationMenu: boolean;
  loadingIds: number[];
  clearAllLoading: boolean;
}

export default defineComponent({
  name: 'NotificationMenu',

  components: { NotificationBadge, AppLoadMoreLoader },
  mixins: [AccountLayoutMixin],

  props: {
    notifications: {
      type: Array as PropType<INotification[]>,
      default: () => []
    },
    total: {
      type: Number,
      default: 0
    },
    hasMore: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  emits: ['clearAllNotifications', 'clearNotification', 'load:more', 'updateNotification'],

  setup () {
    const { addNotification } = useNotifications();

    return { addNotification };
  },

  data: (): INotificationMenu => ({
    notificationMenu: false,
    loadingIds: [],
    clearAllLoading: false
  }),

  computed: {
    ...mapState({
      token: (state: any) => state.idToken
    }),
  },

  watch: {
    total (value) {
      if (!value) {
        this.notificationMenu = false;
      }
    }
  },

  methods: {
    openLink (notification: INotification) {
      if (notification.metadata) {
        window.open(notification.metadata.link, '_blank');
      }

      this.changeNotificationStatus(notification);
    },
    async clearNotification (notification: INotification) {
      try {
        this.loadingIds.push(notification.id);
        await notificationService.updateNotificationStatus(notification.id);
        this.$emit('clearNotification', notification.id);
      } catch (error: any) {
        this.addNotification({ ...error, status: 0 });
      } finally {
        this.loadingIds = this.loadingIds.filter(id => id !== notification.id);
      }
    },
    async changeNotificationStatus (notification: INotification) {
      try {
        this.loadingIds.push(notification.id);
        await notificationService.updateNotificationStatus(notification.id);

        this.$emit('updateNotification', { ...notification, show: true });
      } catch (error: any) {
        this.addNotification({ ...error, status: 0 });
      } finally {
        this.loadingIds = this.loadingIds.filter(id => id !== notification.id);
      }
    },
    checkIsLoading (notificationId: number) {
      return this.loadingIds.some(id => notificationId === id);
    },
    async clearAllNotifications () {
      try {
        this.clearAllLoading = true;
        await notificationService.clearAllNotifications();

        this.$emit('clearAllNotifications');
      } catch (error: any) {
        this.addNotification({ ...error });
      } finally {
        this.clearAllLoading = false;
      }
    }
  }
})
</script>
